import { useNavigate } from 'react-router-dom';
import { ImgPlaceholder } from '../components/ImgPlaceholder';
import { MapSection } from '../components/MapSection';
import { StateMessage } from '../components/StateMessage';
import { useFormateurs, useSiteContent, useTemoignages } from '../data/queries';
import { useSEO } from '../hooks/useSEO';
import type { FormateurCouleur, Tone } from '../types';

const FORMATEUR_TONE: Record<FormateurCouleur, Tone> = {
  vert: 'vert',
  rouge: 'rouge',
  or: 'creme',
};

// Defaults used only when no `siteContent/main` doc exists yet, or while it
// loads. Honest first-cohort numbers — admin can override via the admin app.
const FALLBACK_STATS = [
  { value: '2025', label: 'Année de fondation' },
  { value: '6', label: 'Formations au catalogue' },
  { value: '7', label: 'Enseignants seniors' },
  { value: '8–14', label: 'Praticiens par cohorte' },
];

const FALLBACK_LEDE =
  "Fondé en 2025 à Casablanca, MTC accompagne les chirurgiens-dentistes, jeunes diplômés et étudiants en thèse à travers des programmes de formation continue exigeants. Notre conviction : une formation ne se mesure pas au volume horaire, mais à ce qu'elle change concrètement dans la pratique quotidienne.";

const FALLBACK_ABOUT_TITLE =
  "Le centre marocain de la formation continue dentaire.";

export function About() {
  const navigate = useNavigate();
  const formateursQ = useFormateurs();
  const temoignagesQ = useTemoignages();
  const siteQ = useSiteContent();

  const aboutTitle = siteQ.data?.about?.title?.trim() || FALLBACK_ABOUT_TITLE;
  const aboutLede = siteQ.data?.about?.lede?.trim() || FALLBACK_LEDE;
  const aboutBody = siteQ.data?.about?.body?.trim() ?? '';
  const stats =
    siteQ.data?.stats && siteQ.data.stats.length > 0 ? siteQ.data.stats : FALLBACK_STATS;

  useSEO({
    title: 'À propos',
    description: aboutLede.slice(0, 160),
  });

  if (formateursQ.isLoading || temoignagesQ.isLoading) return <StateMessage variant="loading" />;
  if (formateursQ.error) return <StateMessage variant="error" message={String(formateursQ.error)} />;

  const formateurs = formateursQ.data ?? [];
  const temoignages = temoignagesQ.data ?? [];

  return (
    <div className="page-apropos">
      <section className="ap-hero">
        <span className="mtc-eyebrow">À propos</span>
        <h1>{aboutTitle}</h1>
        <p className="ap-lede">{aboutLede}</p>
      </section>

      {stats.length > 0 && (
        <section className="ap-numbers">
          {stats.map((x, i) => (
            <div key={`${x.label}-${i}`} className="ap-num">
              <div className="ap-num-n">{x.value}</div>
              <div className="ap-num-l">{x.label}</div>
            </div>
          ))}
        </section>
      )}

      {aboutBody && (
        <section className="ap-history">
          <div className="ap-history-grid">
            <div>
              <span className="mtc-eyebrow">Notre histoire</span>
              <h2>{siteQ.data?.about?.title?.trim() || aboutTitle}</h2>
            </div>
            <div className="ap-history-text">
              {aboutBody.split(/\n\s*\n/).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="ap-team">
        <span className="mtc-eyebrow">Comité scientifique</span>
        <h2>Des experts, plusieurs décennies de pratique.</h2>
        <div className="ap-team-grid">
          {formateurs.map((fo) => (
            <button
              key={fo.id}
              type="button"
              className="ap-formateur"
              onClick={() => navigate(`/formateurs/${fo.id}`)}
            >
              <ImgPlaceholder
                label={fo.nom.split(' ').slice(-1)[0] ?? fo.nom}
                ratio="3/4"
                tone={FORMATEUR_TONE[fo.couleur]}
                src={fo.photo}
                alt={fo.nom}
              />
              <div className="ap-formateur-body">
                <h4>{fo.nom}</h4>
                <p>{fo.titre}</p>
                <span className="ap-formateur-link">Voir le profil →</span>
              </div>
            </button>
          ))}
        </div>
      </section>

      <section className="ap-temoins">
        <span className="mtc-eyebrow">Témoignages</span>
        <h2>Ce qu'en disent les praticiens.</h2>
        <div className="ap-temoins-grid">
          {temoignages.map((t, i) => (
            <div key={i} className="ap-temoin">
              <div className="ap-temoin-stars">{'★'.repeat(t.note)}</div>
              <p>« {t.texte} »</p>
              <div className="ap-temoin-attr">
                <strong>{t.auteur}</strong>
                <span>{t.role}</span>
                {t.formation && <span className="ap-temoin-form">— {t.formation}</span>}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="ap-cta">
        <h2>Prêt à rejoindre une session ?</h2>
        <div className="ap-cta-row">
          <button
            type="button"
            className="mtc-btn mtc-btn-primary mtc-btn-lg"
            onClick={() => navigate('/formations')}
          >
            Voir le programme 2026
          </button>
          <button type="button" className="mtc-btn mtc-btn-on-dark" onClick={() => navigate('/inscription')}>
            S'inscrire →
          </button>
        </div>
      </section>

      <MapSection />
    </div>
  );
}
